import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';

import { IMeta } from '../../../core/types/http.interface';

/** A numbered page button, or `null` for an ellipsis between non-adjacent pages. */
type PagerSlot = number | null;

const MAX_UNCOLLAPSED_PAGES = 7;

/**
 * Presentational table-card footer: "1–10 of N items" · chevron / numbered /
 * chevron pager · "Items per page" select. Emits page and limit changes; the
 * container owns the query.
 */
@Component({
  selector: 'em-dashboard-pager',
  templateUrl: './dashboard-pager.component.html',
  styleUrls: ['./dashboard-pager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPagerComponent {
  readonly meta = input.required<IMeta>();
  readonly itemsName = input('items');
  readonly limitOptions = input<readonly number[]>([10, 15, 30, 50]);

  readonly pageChange = output<number>();
  readonly limitChange = output<number>();

  readonly totalPages = computed(() => {
    const { total, limit } = this.meta();
    return Math.max(1, Math.ceil(total / Math.max(1, limit)));
  });

  readonly current = computed(() => Math.min(Math.max(this.meta().current, 1), this.totalPages()));

  readonly rangeLabel = computed(() => {
    const { total, limit } = this.meta();
    if (total <= 0) {
      return `0 ${this.itemsName()}`;
    }
    const start = (this.current() - 1) * limit + 1;
    const end = Math.min(this.current() * limit, total);
    return `${start}–${end} of ${total} ${this.itemsName()}`;
  });

  /** First, last and the current page's neighbours; single-page gaps are filled instead of elided. */
  readonly slots = computed<PagerSlot[]>(() => {
    const total = this.totalPages();
    const current = this.current();
    if (total <= MAX_UNCOLLAPSED_PAGES) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const anchors = [...new Set([1, current - 1, current, current + 1, total])]
      .filter((page) => page >= 1 && page <= total)
      .sort((first, second) => first - second);

    const slots: PagerSlot[] = [];
    let previous = 0;
    for (const page of anchors) {
      if (page - previous === 2) {
        slots.push(previous + 1);
      } else if (page - previous > 2) {
        slots.push(null);
      }
      slots.push(page);
      previous = page;
    }
    return slots;
  });

  goTo(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.current()) {
      return;
    }
    this.pageChange.emit(page);
  }

  onLimitSelect(rawLimit: string): void {
    const limit = Number(rawLimit);
    if (!Number.isFinite(limit) || limit <= 0 || limit === this.meta().limit) {
      return;
    }
    this.limitChange.emit(limit);
  }
}
